package co.maytes.standupbot.dashboard.styles

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.init.InitSilkContext
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.StyleScope

private val compact = CSSMediaQuery.Raw("(max-width: ${Layout.breakpointMd}px)")
private val phone = CSSMediaQuery.Raw("(max-width: ${Layout.breakpointSm}px)")

private fun InitSilkContext.rule(selector: String, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { base { Modifier.styleModifier(build) } }
}

private fun InitSilkContext.mediaRule(selector: String, query: CSSMediaQuery, build: StyleScope.() -> Unit) {
    stylesheet.registerStyle(selector) { cssRule(query, null) { Modifier.styleModifier(build) } }
}

private fun StyleScope.mask(url: String) {
    property("-webkit-mask-image", "url($url)")
    property("mask-image", "url($url)")
    property("-webkit-mask-size", "contain")
    property("mask-size", "contain")
    property("-webkit-mask-repeat", "no-repeat")
    property("mask-repeat", "no-repeat")
    property("-webkit-mask-position", "center")
    property("mask-position", "center")
}

internal fun InitSilkContext.registerMarketingArt() {
    registerArtTints()
    registerArtGlyphs()
    registerHomeHero()
    registerHomeTagline()
    registerHomeSteps()
    registerHomeBenefits()
    registerHomeTrust()
    registerHomeCalculator()
    registerHomeFaq()
    registerHomeClosing()
}

private fun InitSilkContext.registerArtTints() {
    mapOf(
        "linear" to Marketing.BRAND_LINEAR,
        "github" to Marketing.BRAND_GITHUB,
        "claude" to Marketing.BRAND_CLAUDE,
        "slack" to Marketing.BRAND_SLACK,
        "mint" to Marketing.BRAND_MINT,
        "accent" to "${Palette.accent}",
    ).forEach { (name, color) -> rule(".mk-tint--$name") { property("--mk-tint", color) } }
    rule(".mk-layer") {
        property("position", "relative")
    }
}

private fun InitSilkContext.registerArtGlyphs() {
    rule(".mk-tile") {
        property("flex", "none")
        property("display", "inline-grid")
        property("place-items", "center")
        property("width", "2rem")
        property("height", "2rem")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-tile--lg") {
        property("width", "3.5rem")
        property("height", "3.5rem")
    }
    rule(".mk-glyph") {
        property("display", "block")
        property("width", "1.1rem")
        property("height", "1.1rem")
    }
    rule(".mk-tile--lg .mk-glyph") {
        property("width", "1.75rem")
        property("height", "1.75rem")
    }
    rule(".mk-glyph--github") {
        property("background-color", "${Palette.textPrimary}")
        mask("/brand/github.svg")
    }
    rule(".mk-ico") {
        property("flex", "none")
        property("display", "inline-block")
        property("width", "1.1em")
        property("height", "1.1em")
        property("background-color", "currentColor")
    }
    MarketingIcon.entries.forEach { icon ->
        rule(".mk-ico--${icon.slug}") { mask("/marketing/icons/${icon.slug}.svg") }
    }
    rule(".mk-tile .mk-ico") {
        property("width", "1.1rem")
        property("height", "1.1rem")
    }
    rule(".mk-tile--lg .mk-ico") {
        property("width", "1.5rem")
        property("height", "1.5rem")
    }
    rule(".mk-chip--icon") {
        property("display", "inline-flex")
        property("align-items", "center")
        property("gap", "${Spacing.xxs}")
    }
}

private fun InitSilkContext.registerHomeHero() {
    rule(".mk-hero-inner") {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("text-align", "center")
        property("gap", "${Spacing.lg}")
    }
    rule(".mk-hero-inner .mk-display") {
        property("font-size", Marketing.FONT_H1)
        property("max-width", HomeTokens.HERO_MEASURE)
        property("background-image", "linear-gradient(90deg, ${Palette.textPrimary} 35%, ${Palette.textSecondary})")
        property("-webkit-background-clip", "text")
        property("background-clip", "text")
        property("-webkit-text-fill-color", "transparent")
        property("color", "transparent")
    }
    rule(".mk-hero-inner .mk-lede, .mk-section-head .mk-lede") {
        property("text-align", "center")
    }
    rule(".mk-hero-inner .mk-actions") {
        property("justify-content", "center")
    }
    rule(".mk-proof") {
        property("margin", "0")
        property("font-size", "${Typography.bodySmall.size}")
        property("color", "${Palette.textSecondary}")
    }
    rule(".mk-stagefield") {
        property("background-color", "${Palette.surfaceSubtle}")
        property("padding-top", Marketing.SECTION_PAD_TIGHT)
        property("padding-bottom", Marketing.SECTION_PAD_TIGHT)
    }
    rule(".mk-showcase") {
        property("width", "100%")
        property("max-width", Marketing.SHOWCASE_MEASURE)
        property("margin-left", "auto")
        property("margin-right", "auto")
    }
    mediaRule(".mk-hero-inner .mk-actions", phone) {
        property("flex-direction", "column")
        property("align-items", "stretch")
        property("width", "100%")
        property("max-width", "20rem")
    }
}

private fun InitSilkContext.registerHomeTagline() {
    rule(".mk-tagline-section") {
        padding(Marketing.SECTION_PAD, Marketing.SECTION_PAD_TIGHT)
    }
    rule(".mk-tagline") {
        property("margin", "0 auto")
        property("max-width", HomeTokens.TAGLINE_MEASURE)
        property("text-align", "center")
        property("font-size", Marketing.FONT_H2)
        property("font-weight", "${FontWeight.semibold}")
        property("line-height", "1.14")
        property("letter-spacing", "-0.035em")
        property("color", "${Palette.textPrimary}")
        property("text-wrap", "balance")
    }
    rule(".mk-tagline--live .mk-tw") {
        property("opacity", HomeTokens.TAGLINE_DIM)
        property("transition", Motion.transitionWith(HomeTokens.DUR_TAGLINE, Motion.EASE_EMPHASIS, "opacity"))
    }
    rule(".mk-tagline--live .mk-tw.mk-tw--lit") {
        property("opacity", "1")
    }
}

private fun InitSilkContext.registerHomeSteps() {
    rule(".mk-steps-section") {
        padding(Marketing.SECTION_PAD_TIGHT, Marketing.SECTION_PAD)
    }
    rule(".mk-how") {
        property("display", "grid")
        property("grid-template-columns", "repeat(3, minmax(0, 1fr))")
        property("gap", "${Spacing.xxl}")
    }
    mediaRule(".mk-how", compact) {
        property("grid-template-columns", "minmax(0, 1fr)")
        property("gap", "${Spacing.xl}")
    }
    rule(".mk-how-item") {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "flex-start")
        property("gap", "${Spacing.sm}")
    }
    rule(".mk-logos") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Spacing.md}")
        property("height", HomeTokens.LOGO_SIZE)
        property("margin-bottom", "${Spacing.xs}")
    }
    rule(".mk-logo .mk-glyph") {
        property("width", HomeTokens.LOGO_SIZE)
        property("height", HomeTokens.LOGO_SIZE)
    }
    rule(".mk-how-title, .mk-benefit-title") {
        property("margin", "0")
        property("font-weight", "${FontWeight.semibold}")
        property("font-size", HomeTokens.FONT_ITEM_TITLE)
        property("line-height", "1.2")
        property("letter-spacing", "-0.025em")
        property("color", "${Palette.textPrimary}")
        property("text-wrap", "balance")
    }
    rule(".mk-how-item .mk-body, .mk-benefit .mk-body") {
        property("text-wrap", "pretty")
        property("max-width", Marketing.COPY_MEASURE)
    }
}

private fun InitSilkContext.registerHomeBenefits() {
    rule(".mk-benefits") {
        property("display", "grid")
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        property("gap", "${Spacing.xxl} ${Spacing.x3l}")
        property("max-width", HomeTokens.BENEFITS_MEASURE)
        property("margin", "0 auto")
    }
    mediaRule(".mk-benefits", compact) {
        property("grid-template-columns", "minmax(0, 1fr)")
        property("gap", "${Spacing.xl}")
    }
    rule(".mk-benefit") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Spacing.xs}")
    }
}

private fun InitSilkContext.registerHomeTrust() {
    rule(".mk-caps") {
        property("list-style", "none")
        property("margin", "0 auto")
        property("padding", "0")
        property("max-width", HomeTokens.CAPS_MEASURE)
        property("display", "grid")
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        property("gap", "${Spacing.md} ${Spacing.xxl}")
    }
    mediaRule(".mk-caps", compact) {
        property("grid-template-columns", "minmax(0, 1fr)")
    }
    rule(".mk-cap") {
        property("display", "flex")
        property("align-items", "center")
        property("gap", "${Spacing.sm}")
        property("font-size", "${Typography.body.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-caps-link") {
        property("display", "flex")
        property("justify-content", "center")
        property("margin-top", "${Spacing.xl}")
    }
}

private fun InitSilkContext.registerHomeClosing() {
    rule(".mk-closing") {
        property("background-color", HomeTokens.CLOSE_FIELD)
        property("color", Marketing.CLOSE_WHITE)
        property("padding-top", Marketing.SECTION_PAD)
        property("padding-bottom", Marketing.SECTION_PAD)
    }
    rule(".mk-closing-inner") {
        property("display", "flex")
        property("flex-direction", "column")
        property("align-items", "center")
        property("text-align", "center")
        property("gap", "${Spacing.lg}")
    }
    rule(".mk-closing .mk-h2") {
        property("color", Marketing.CLOSE_WHITE)
    }
    rule(".mk-closing .mk-lede") {
        property("color", Marketing.CLOSE_MUTED)
        property("text-align", "center")
    }
    rule(".mk-closing .mk-actions") {
        property("justify-content", "center")
    }
    mediaRule(".mk-closing .mk-actions", phone) {
        property("flex-direction", "column")
        property("align-items", "stretch")
        property("width", "100%")
        property("max-width", "20rem")
    }
}

private fun InitSilkContext.registerHomeCalculator() {
    rule(".mk-calc") {
        property("display", "grid")
        property("grid-template-columns", "repeat(2, minmax(0, 1fr))")
        property("gap", "${Spacing.xxl}")
        property("align-items", "center")
        property("max-width", HomeTokens.CALC_MEASURE)
        property("margin", "0 auto")
    }
    mediaRule(".mk-calc", compact) {
        property("grid-template-columns", "minmax(0, 1fr)")
        property("gap", "${Spacing.xl}")
    }
    rule(".mk-calc-controls") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Spacing.lg}")
    }
    rule(".mk-calc-field") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Spacing.xs}")
    }
    rule(".mk-calc-label") {
        property("display", "flex")
        property("justify-content", "space-between")
        property("font-size", "${Typography.body.size}")
        property("font-weight", "${FontWeight.medium}")
        property("color", "${Palette.textPrimary}")
    }
    rule(".mk-calc-value") {
        property("font-variant-numeric", "tabular-nums")
    }
    rule(".mk-calc-field input[type=range]") {
        property("width", "100%")
        property("accent-color", "${Palette.textPrimary}")
        property("cursor", "pointer")
    }
    rule(".mk-calc-result") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Spacing.xs}")
    }
    rule(".mk-calc-figure") {
        property("font-size", Marketing.FONT_DISPLAY)
        property("font-weight", "${FontWeight.semibold}")
        property("line-height", "1")
        property("letter-spacing", "-0.045em")
        property("font-variant-numeric", "tabular-nums")
        property("color", "${Palette.textPrimary}")
    }
}

private fun InitSilkContext.registerHomeFaq() {
    rule(".mk-faq") {
        property("display", "flex")
        property("flex-direction", "column")
        property("gap", "${Spacing.xs}")
        property("max-width", HomeTokens.FAQ_MEASURE)
        property("margin", "0 auto")
    }
    rule(".mk-price-strip .mk-lede") {
        property("text-align", "center")
    }
}

private fun StyleScope.padding(top: String, bottom: String) {
    property("padding-top", top)
    property("padding-bottom", bottom)
}
